import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getLog, getMessages } from "./api/newTelemetry";

// Distinct colors per log; index 0 (the base) is the app accent.
const COLORS = ["#3a8eff", "#f2994a", "#27ae60", "#eb5757", "#9b51e0", "#00bcd4", "#bdbdbd"];

const chartableTypes = (messageIndex) =>
  Object.entries(messageIndex || {})
    .filter(([, meta]) => (meta.fields || []).includes("TimeUS") && (meta.fields || []).length > 1)
    .map(([name]) => name)
    .sort();

/**
 * Overlays the same signal (message type + field) from several logs on one chart.
 * Each log keeps its own time base, normalised to seconds-from-start; lines use
 * connectNulls so interleaved rows still render as continuous per-log lines.
 */
const LogCompareCharts = ({ logs }) => {
  const theme = useTheme();
  const [messageIndex, setMessageIndex] = useState(null); // from the base log
  const [type, setType] = useState("");
  const [field, setField] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Base log (logs[0]) defines which types/fields are offered.
  useEffect(() => {
    if (!logs?.length) return;
    let cancelled = false;
    getLog(logs[0].id)
      .then((log) => !cancelled && setMessageIndex(log.message_index || {}))
      .catch(() => !cancelled && setMessageIndex({}));
    return () => {
      cancelled = true;
    };
  }, [logs]);

  const types = useMemo(() => chartableTypes(messageIndex), [messageIndex]);

  useEffect(() => {
    if (!types.length) return;
    const preferred = ["ATT", "GPS", "BAT", "VIBE"].find((t) => types.includes(t));
    setType((cur) => cur || preferred || types[0]);
  }, [types]);

  const fields = useMemo(() => (messageIndex?.[type]?.fields || []).filter((f) => f !== "TimeUS"), [messageIndex, type]);

  useEffect(() => {
    if (!fields.length) return;
    setField((cur) => (fields.includes(cur) ? cur : fields[0]));
  }, [fields]);

  useEffect(() => {
    if (!type || !field || !logs?.length) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(
      logs.map((l) =>
        getMessages(l.id, { type, fields: [field], maxPoints: 500 })
          .then((data) => ({ id: l.id, points: data.points || [] }))
          .catch(() => ({ id: l.id, points: [] }))
      )
    )
      .then((results) => {
        if (cancelled) return;
        // Merge into interleaved rows keyed by seconds-from-start; one log value per row.
        const merged = [];
        results.forEach(({ id, points }) => {
          if (!points.length) return;
          const t0 = points[0].TimeUS ?? 0;
          points.forEach((p) => merged.push({ t: (p.TimeUS - t0) / 1e6, [id]: p[field] }));
        });
        merged.sort((a, b) => a.t - b.t);
        setRows(merged);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [type, field, logs]);

  const nameById = useMemo(() => Object.fromEntries(logs.map((l) => [l.id, l.file_name])), [logs]);

  if (messageIndex && !types.length) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        No chartable data in the base log.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Message</InputLabel>
          <Select label="Message" value={type} onChange={(e) => setType(e.target.value)}>
            {types.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Field</InputLabel>
          <Select label="Field" value={field} onChange={(e) => setField(e.target.value)}>
            {fields.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ height: 320, position: "relative" }}>
        {loading && (
          <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 1 }}>
            <CircularProgress color="secondary" size={24} />
          </Box>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              stroke={theme.palette.divider}
              tickFormatter={(t) => `${Math.round(t)}s`}
            />
            <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} stroke={theme.palette.divider} width={56} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                color: theme.palette.text.primary,
              }}
              labelFormatter={(t) => `${Math.round(t)}s`}
            />
            <Legend />
            {logs.map((l, i) => (
              <Line
                key={l.id}
                type="monotone"
                dataKey={l.id}
                name={nameById[l.id]}
                stroke={COLORS[i % COLORS.length]}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default LogCompareCharts;
